import React, { useState, useEffect } from "react";
import { Modal, Tabs, Tab, Row, Col, Form, Button, Card } from "react-bootstrap";
import { getRequest, patchRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import ServiceFormHeader from "./ServiceFormHeader";
import ServiceBasicInfo from "./ServiceBasicInfo";
import InventorySelector from "./InventorySelector";
// import TemplateSelector from "./TemplateSelector";
import SelectedItemsSummary from "./SelectedItemsSummary";

const ServiceForm = ({
  open,
  onOpenChange,
  onSubmit,
  atvId, // ATV ID for the service
  serviceId, // Service ID for editing
}) => {
  const [formData, setFormData] = useState(() => {
    const today = new Date();
    const nextServiceDate = new Date(today);
    nextServiceDate.setMonth(nextServiceDate.getMonth() + 2);

    return {
      atv_id: atvId || "",
      mechanic_id: "",
      warehouse_id: "",
      service_date: today.toISOString().split("T")[0], // Today's date
      next_service: nextServiceDate.toISOString().split("T")[0], // 2 months from today
      description: "",
      service_items: [],
    };
  });

  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState("inventory");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);

  // Manual item states
  const [manualItemName, setManualItemName] = useState("");
  const [manualItemQuantity, setManualItemQuantity] = useState(1);
  const [manualItemUnit, setManualItemUnit] = useState("pcs");

  // Auto-calculate next service date (2 months from service date)
  useEffect(() => {
    if (formData.service_date) {
      const serviceDate = new Date(formData.service_date);
      const nextServiceDate = new Date(serviceDate);
      nextServiceDate.setMonth(nextServiceDate.getMonth() + 2);

      const nextServiceDateString = nextServiceDate.toISOString().split("T")[0];
      setFormData((prev) => ({
        ...prev,
        next_service: nextServiceDateString,
      }));
    }
  }, [formData.service_date]);

  // Load service data for editing
  useEffect(() => {
    if (serviceId && open) {
      const fetchServiceData = async () => {
        try {
          const response = await getRequest(`/service/${serviceId}`);
          if (response.success && response.data) {
            const service = response.data;
            setFormData({
              atv_id: service.atv_id,
              mechanic_id: service.mechanic_id,
              warehouse_id: service.warehouse_id || "",
              service_date: service.service_date.split("T")[0],
              next_service: service.next_service
                ? service.next_service.split("T")[0]
                : "",
              description: service.description,
              service_items: service.service_items || [],
            });
            setSelectedItems(service.service_items || []);
          } else {
            throw new Error(response.message || "Failed to fetch service");
          }
        } catch (error) {
          console.error("Error fetching service data:", error);
          setError({ message: "Failed to load service data." });
          setShowErrorDialog(true);
        }
      };
      fetchServiceData();
    } else if (!serviceId && open) {
      // Reset form for new service
      const today = new Date();
      const nextServiceDate = new Date(today);
      nextServiceDate.setMonth(nextServiceDate.getMonth() + 2);

      setFormData({
        atv_id: atvId || "",
        mechanic_id: "",
        warehouse_id: "",
        service_date: today.toISOString().split("T")[0],
        next_service: nextServiceDate.toISOString().split("T")[0],
        description: "",
        service_items: [],
      });
      setSelectedItems([]);
      // Reset manual item form
      setManualItemName("");
      setManualItemQuantity(1);
      setManualItemUnit("pcs");
    }
  }, [serviceId, open, atvId]);

  // Load inventory options for AsyncSelect
  const loadInventoryOptions = async (inputValue) => {
    try {
      const queryParams = {
        search: inputValue || "",
        include: "category",
        limit: 1000,
      };
      const queryString = buildQueryString(queryParams);
      const response = await getRequest(`/inventory${queryString}`);

      if (response.success && response.data) {
        setInventoryItems(response.data); // Store inventory items for validation
        return response.data.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.unit}) - ${
            item.category?.name || "No Category"
          }`,
          item: item,
        }));
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error loading inventory options:", error);
      return [];
    }
  };

  // Helper function to get BMK warehouse stock
  const getBMKWarehouseStock = (item) => {
    if (!item.warehouse_stocks || !Array.isArray(item.warehouse_stocks)) {
      return 0;
    }
    const bmkWarehouse = item.warehouse_stocks.find(
      (ws) => ws.warehouse_code === "BMK"
    );
    return bmkWarehouse ? bmkWarehouse.stock : 0;
  };

  const addItem = (selectedOption) => {
    if (!selectedOption) return;

    const item = selectedOption.item;
    const bmkStock = getBMKWarehouseStock(item);
    const existing = selectedItems.find((i) => i.inventory_id === item.id);
    const currentQuantity = existing ? existing.quantity : 0;
    const newQuantity = currentQuantity + 1;

    // Validate stock availability
    if (newQuantity > bmkStock) {
      setError({
        message: `Stok tidak mencukupi. Stok tersedia di Bengkel Mekanik: ${bmkStock} ${
          item.unit || "pcs"
        }`,
      });
      setShowErrorDialog(true);
      return;
    }

    if (existing) {
      // Increase quantity if item already exists
      setSelectedItems((prev) =>
        prev.map((i) =>
          i.inventory_id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      // Add new item
      const newItem = {
        inventory_id: item.id,
        item_name: item.name,
        quantity: 1,
        unit: item.unit || "pcs",
        key: `inventory-${item.id}`, // Unique key for inventory items
      };
      setSelectedItems((prev) => [...prev, newItem]);
    }
  };

  const removeItem = (inventoryId) => {
    setSelectedItems((prev) =>
      prev
        .map((i) =>
          i.inventory_id === inventoryId
            ? { ...i, quantity: i.quantity - 1 }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const updateItemQuantity = (inventoryId, quantity) => {
    if (quantity <= 0) {
      removeItem(inventoryId);
      return;
    }

    // Find the inventory item to check stock
    const inventoryItem = inventoryItems.find(
      (item) => item.id === inventoryId
    );
    if (inventoryItem) {
      const bmkStock = getBMKWarehouseStock(inventoryItem);
      if (quantity > bmkStock) {
        setError({
          message: `Stok tidak mencukupi. Stok tersedia di Bengkel Mekanik: ${bmkStock} ${
            inventoryItem.unit || "pcs"
          }`,
        });
        setShowErrorDialog(true);
        return;
      }
    }

    setSelectedItems((prev) =>
      prev.map((i) => (i.inventory_id === inventoryId ? { ...i, quantity } : i))
    );
  };

  // const applyTemplate = (template) => {
  //   // Convert template items to service items format and merge with existing items
  //   const templateItems = template.template_items.map((item) => ({
  //     inventory_id: item.inventory_id,
  //     item_name: item.item_name,
  //     quantity: item.quantity,
  //     unit: item.unit,
  //   }));

  //   // Merge template items with existing selected items
  //   setSelectedItems((prevItems) => {
  //     const mergedItems = [...prevItems];

  //     templateItems.forEach((templateItem) => {
  //       const existingIndex = mergedItems.findIndex(
  //         (item) => item.inventory_id === templateItem.inventory_id
  //       );

  //       if (existingIndex >= 0) {
  //         // Item already exists, increase quantity
  //         mergedItems[existingIndex].quantity += templateItem.quantity;
  //       } else {
  //         // Add new item
  //         mergedItems.push(templateItem);
  //       }
  //     });

  //     return mergedItems;
  //   });

  //   // Update description only if it's empty
  //   setFormData((prev) => ({
  //     ...prev,
  //     description: prev.description || template.description,
  //   }));
  // };

  const addManualItem = () => {
    if (!manualItemName.trim()) {
      setError({
        message: "Nama item manual tidak boleh kosong.",
      });
      setShowErrorDialog(true);
      return;
    }

    if (manualItemQuantity <= 0) {
      setError({
        message: "Quantity harus lebih dari 0.",
      });
      setShowErrorDialog(true);
      return;
    }

    // Check if manual item with same name already exists
    const existing = selectedItems.find(
      (i) => !i.inventory_id && i.item_name === manualItemName.trim()
    );

    if (existing) {
      // Increase quantity if item already exists
      setSelectedItems((prev) =>
        prev.map((i) =>
          !i.inventory_id && i.item_name === manualItemName.trim()
            ? { ...i, quantity: i.quantity + manualItemQuantity }
            : i
        )
      );
    } else {
      // Add new manual item
      const newItem = {
        item_name: manualItemName.trim(),
        quantity: manualItemQuantity,
        unit: manualItemUnit,
        key: `manual-${Date.now()}`, // Unique key for manual items
      };
      setSelectedItems((prev) => [...prev, newItem]);
    }

    // Reset manual item form
    setManualItemName("");
    setManualItemQuantity(1);
    setManualItemUnit("pcs");
  };

  const updateManualItemQuantity = (key, quantity) => {
    if (quantity <= 0) {
      removeManualItem(key);
      return;
    }
    setSelectedItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity } : i))
    );
  };

  const removeManualItem = (key) => {
    setSelectedItems((prev) => prev.filter((i) => i.key !== key));
  };

  const clearAllItems = () => {
    setSelectedItems([]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Transform selectedItems to match API format
      const serviceItems = selectedItems.map(item => {
        if (item.inventory_id) {
          // Inventory item
          return {
            inventory_id: item.inventory_id,
            item_name: item.item_name,
            quantity: item.quantity,
            unit: item.unit,
          };
        } else {
          // Manual item
          return {
            item_name: item.item_name,
            quantity: item.quantity,
            unit: item.unit,
          };
        }
      });

      const dataToSubmit = {
        ...formData,
        service_items: serviceItems,
      };

      if (serviceId) {
        // Update existing service
        const response = await patchRequest(
          `/service/${serviceId}`,
          dataToSubmit
        );
        if (!response.success) {
          throw new Error(response.message || "Failed to update service");
        }
        await onSubmit(dataToSubmit, serviceId);
      } else {
        // Create new service
        await onSubmit(dataToSubmit);
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting service:", error);
      let errorMessage = "Failed to save service. Please try again.";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error
      ) {
        errorMessage = error.response.data.error;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError({ message: errorMessage });
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // const totalCount = selectedItems.reduce(
  //   (sum, item) => sum + item.quantity,
  //   0
  // );
  // const totalCost = selectedItems.reduce(
  //   (sum, item) => sum + item.price * item.qty,
  //   0
  // );

  const isFormValid =
    formData.mechanic_id &&
    // formData.description.trim() &&
    selectedItems.length > 0;

  return (
    <>
      <Modal
        show={open}
        onHide={() => onOpenChange(false)}
        fullscreen={true}
        style={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <ServiceFormHeader onClose={() => onOpenChange(false)} />
          
        <Modal.Body>
          <ServiceBasicInfo formData={formData} setFormData={setFormData} />

          <Row className="g-4" style={{ minHeight: "400px" }}>
            {/* Left side - Item Selection */}
            <Col md={12} lg={8}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
              >
                <Tab eventKey="inventory" title="Pilih Inventory">
                  <InventorySelector
                    loadInventoryOptions={loadInventoryOptions}
                    onAddItem={addItem}
                  />
                </Tab>

                <Tab eventKey="manual" title="Manual Items">
                  <Card className="p-3">
                    <h6 className="mb-3">Quick Add Manual Items</h6>
                    <div className="mb-4">
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => {
                            setManualItemName("SERVICE RINGAN");
                            setManualItemQuantity(1);
                            setManualItemUnit("pcs");
                          }}
                        >
                          SERVICE RINGAN
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setManualItemName("SERVICE SEDANG");
                            setManualItemQuantity(1);
                            setManualItemUnit("pcs");
                          }}
                        >
                          SERVICE SEDANG
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => {
                            setManualItemName("SERVICE BERAT");
                            setManualItemQuantity(1);
                            setManualItemUnit("pcs");
                          }}
                        >
                          SERVICE BERAT
                        </Button>
                      </div>
                    </div>

                    <h6 className="mb-3">Atau Tambah Item Manual Custom</h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Nama Item</Form.Label>
                          <Form.Control
                            type="text"
                            value={manualItemName}
                            onChange={(e) => setManualItemName(e.target.value)}
                            placeholder="Contoh: Custom Labor, Additional Service"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Quantity</Form.Label>
                          <Form.Control
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={manualItemQuantity}
                            onChange={(e) =>
                              setManualItemQuantity(parseFloat(e.target.value) || 1)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Unit</Form.Label>
                          <Form.Select
                            value={manualItemUnit}
                            onChange={(e) => setManualItemUnit(e.target.value)}
                          >
                            <option value="pcs">pcs</option>
                            <option value="hours">hours</option>
                            <option value="liter">liter</option>
                            <option value="kg">kg</option>
                            <option value="meter">meter</option>
                            <option value="box">box</option>
                            <option value="pack">pack</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <Button
                        variant="primary"
                        onClick={addManualItem}
                        disabled={!manualItemName.trim()}
                      >
                        Tambah Item Manual
                      </Button>
                    </div>
                  </Card>
                </Tab>

                {/* <Tab eventKey="templates" title="Template Service">
                  <TemplateSelector onApplyTemplate={applyTemplate} />
                </Tab> */}
              </Tabs>
            </Col>

            {/* Right side - Selected Items Summary */}
            <Col md={12} lg={4}>
              <SelectedItemsSummary
                selectedItems={selectedItems}
                onUpdateQuantity={updateItemQuantity}
                onRemoveItem={removeItem}
                onUpdateManualItemQuantity={updateManualItemQuantity}
                onRemoveManualItem={removeManualItem}
                onClearAll={clearAllItems}
                onSubmit={handleSubmit}
                loading={loading}
                isFormValid={isFormValid}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* Error Dialog */}
      <Modal
        show={showErrorDialog}
        onHide={() => setShowErrorDialog(false)}
        centered
        className="modal-blur"
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title className="modal-title">
            <svg
              className="icon icon-tabler icon-tabler-alert-triangle me-2"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M12 9v2m0 4v.01"></path>
              <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
            </svg>
            Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <div className="text-center">
            <div className="mb-3">
              <svg
                className="icon icon-tabler icon-tabler-alert-circle text-danger"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <circle cx="12" cy="12" r="9"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h5 className="modal-title mb-3">Service Submission Failed</h5>
            <p className="text-muted mb-0">
              {error?.message ||
                "An unexpected error occurred while submitting the service."}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowErrorDialog(false)}
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ServiceForm;
